import { LogLevel, OpenIdConfiguration } from 'angular-auth-oidc-client';
import { AppConfigService } from './app-config.service';

/**
 * Factory function to generate OIDC configuration from AppConfig.
 * This allows the OIDC configuration to be loaded from the runtime config
 * instead of being hardcoded in the application.
 *
 * @param configService - The application configuration service
 * @returns OpenIdConfiguration object for angular-auth-oidc-client
 */
export function createOidcConfig(configService: AppConfigService): OpenIdConfiguration {
  const config = configService.getConfig();

  // Derive OIDC authority from environment if not explicitly set
  // Matches BackgroundSolutions pattern: QA uses accounts.qa.neogov.net, prod uses accounts.neogov.com
  const authority = config.oidcAuthority || deriveAccountsUrl(config.environment);

  return {
    authority,
    redirectUrl: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    clientId: config.oidcClientId || 'hcmiweb',
    scope: 'openid profile email offline_access',
    responseType: 'code',
    silentRenew: true,
    useRefreshToken: true,
    renewTimeBeforeTokenExpiresInSeconds: 15,
    ignoreNonceAfterRefresh: true,
    triggerRefreshWhenIdTokenExpired: false,
    logLevel: LogLevel.Debug,
    configId: config.oidcConfigId || 'hcmiweb',
  };
}

/**
 * Derives the accounts/identity server URL from the environment name.
 * - prod → https://accounts.neogov.com
 * - qa/staging/dev → https://accounts.{env}.neogov.net
 * - local → https://accounts.qa.neogov.net (fallback for local dev)
 */
function deriveAccountsUrl(environment?: string): string {
  if (environment === 'prod') {
    return 'https://accounts.neogov.com';
  }
  if (environment && environment !== 'local') {
    return `https://accounts.${environment}.neogov.net`;
  }
  return 'https://accounts.qa.neogov.net';
}
